<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { fetchAccountSingle, fetchClients, updateAccount, VerifyAdmin } from '@/api/accountsAdmin'
import { useAccountStore } from '@/stores/accountStore'

import { useCreateAccountStore } from '@/stores/createAccountStore'
import ProgressLoader from '@/components/ProgressLoader.vue'
import logoPlaceholder from '@/views/logo-placeholder.png'
import plusIcon from '@/views/plus-icon.jpg'
import { dealerships } from '@/views/PaCode.js'
import {
  getRequiredBillingErrors,
  requiredTrimmedRule,
  trimRequiredBillingFields,
} from '@/utils/billingValidation'

const dash = (v: any) => ((v ?? '') === '' ? '—' : String(v))

const showError = (msg: string) => {
  snackbar.value.message = msg
  snackbar.value.color = 'error'
  snackbar.value.show = true
}

const accountTypeItems = ['unspecified', 'Ford Direct', 'Independent', 'Group', 'OEM']

const formRef = ref()
const billingRules = {
  address_1: requiredTrimmedRule('Address 1'),
  city: requiredTrimmedRule('City'),
  state: requiredTrimmedRule('State'),
  zip: requiredTrimmedRule('Zip'),
}

const router = useRouter()
const accountStore = useAccountStore()
const route = useRoute()
const store = useCreateAccountStore()
const loading = ref(false)
const isLoading = ref(true)
const clients = ref([])
const imageTouched = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const base64Image = ref('')
const searchQueryDealerPa = ref('')

import { timeZones as region } from '@/views/timezoneData.js'

const timeZones = ref(region)
const timezoneSearchQuery = ref('')

const snackbar = ref({
  show: false,
  message: '' as string,
  color: 'error' as 'error' | 'success' | 'info',
})

const urlRule = (v) => {
  if (!v) return true
  const pattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i
  return pattern.test(v) || 'Please enter a valid URL (must start with http:// or https://)'
}

const accountName = route.params.name as string
const isParentAccount = computed(() => {
  return (
    formFields.value.accountName ===
    formFields.value.parentAccount?.toLowerCase().replace(/[-\s]/g, '_')
  )
})

const formFields = ref({
  isParent: false,
  parentAccount: '',
  image: '',
})
const a5AccountInput = ref('')
const originalData = ref<any>(null)

const IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/jpg']
const IMAGE_MAX_MB = 2
const IMAGE_MAX_BYTES = IMAGE_MAX_MB * 1024 * 1024

const imageRules = [
  (f: any) => {
    if (!f || (Array.isArray(f) && f.length === 0)) return true
    const file = Array.isArray(f) ? f[0] : f
    if (!IMAGE_ACCEPT.includes(file?.type)) return 'Logo must be PNG or JPG'
    if (file?.size > IMAGE_MAX_BYTES) return `Max ${IMAGE_MAX_MB}MB`
    return true
  },
]

const onLogoChange = (fileOrFiles: File | File[] | null) => {
  let file: File | null = null
  if (fileOrFiles instanceof File) file = fileOrFiles
  else if (Array.isArray(fileOrFiles) && fileOrFiles[0]) file = fileOrFiles[0]

  if (!file) {
    imageTouched.value = false
    formFields.value.image = ''
    return
  }
  if (!IMAGE_ACCEPT.includes(file.type) || file.size > IMAGE_MAX_BYTES) {
    snackbar.value.message = 'Please choose a PNG/JPG under 2MB.'
    snackbar.value.color = 'error'
    snackbar.value.show = true
    return
  }
  imageTouched.value = true
  const reader = new FileReader()
  reader.onload = () => {
    formFields.value.image = reader.result as string
  }
  reader.readAsDataURL(file)
}

const clearLogo = () => {
  imageTouched.value = true
  formFields.value.image = ''
}

const fetchClientList = async () => {
  try {
    const res = await fetchClients()
    clients.value = res.map((client) => ({ title: client }))
  } catch (error) {
    console.error('Failed to load clients:', error)
  }
}

const prettyUrl = (v?: string) => {
  if (!v) return ''
  try {
    const u = new URL(v.startsWith('http') ? v : `https://${v}`)
    return u.origin + (u.pathname !== '/' ? u.pathname : '/')
  } catch {
    return v
  }
}

const formatPhone = (raw: string) => {
  const s = (raw || '').replace(/\D/g, '').slice(0, 15)
  if (s.length <= 3) return s
  if (s.length <= 6) return `(${s.slice(0, 3)}) ${s.slice(3)}`
  return (
    `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6, 10)}` +
    (s.length > 10 ? ` x${s.slice(10)}` : '')
  )
}

const onPhoneInput = (key: 'primaryContact.phone') => {
  const path = key.split('.')
  let obj: any = formFields.value
  for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]]
  const last = path[path.length - 1]
  obj[last] = formatPhone(obj[last] || '')
}

watch(
  () => formFields.value.mainWebsite,
  (v) => {
    const ok = urlRule(v) === true
    if (ok) formFields.value.mainWebsite = prettyUrl(v)
  }
)

const fetchAccountDetails = async () => {
  isLoading.value = true
  try {
    const data = await fetchAccountSingle(accountName)
    formFields.value = {
      ...data,
      isParent: false,
    }

    if (data.a5Account) {
      a5AccountInput.value = Array.isArray(data.a5Account)
        ? data.a5Account.join(',')
        : String(data.a5Account)
    }
    await fetchClientList()

    originalData.value = JSON.parse(JSON.stringify(formFields.value))
  } catch (error) {
    console.error('Failed to fetch account data:', error)
  } finally {
    isLoading.value = false
  }
}

const submitForm = async () => {
  const errors: string[] = []
  if (!formFields.value.accountNameFriendly?.trim()) errors.push('Account Name is required.')
  if (!formFields.value.parentAccount?.trim()) errors.push('Parent Account is required.')
  const billing = (formFields.value as unknown as { billing?: Record<string, unknown> }).billing
  errors.push(...getRequiredBillingErrors(billing))

  const urlCheck = urlRule(formFields.value.mainWebsite)
  if (urlCheck !== true) errors.push(urlCheck as string)

  if (formFields.value.accountType === 'Ford Direct') {
    if (!formFields.value.dealerPA) errors.push('Dealer PA is required.')
  } else if (!formFields.value.accountType) {
    errors.push('Account Type is required.')
  }

  const { valid } = await formRef.value.validate()
  if (!valid || errors.length) {
    showError(errors[0] || 'Please fill all required fields.')
    return
  }

  loading.value = true
  try {
    const payload = preparePayload(formFields.value)
    await updateAccount(payload)

    snackbar.value.message = '✅ Account updated successfully!'
    snackbar.value.color = 'success'
    snackbar.value.show = true

    setTimeout(() => {
      try {
        accountStore.setAccountsCache([])
      } catch {}
      router.push(`/account-admin/${accountName}`)
    }, 900)
  } catch (error) {
    console.error('Failed to update account:', error)
    showError('Failed to update account.')
  } finally {
    loading.value = false
  }
}

const preparePayload = (payload: any) => {
  const adjustedPayload = {
    ...payload,
    billing: trimRequiredBillingFields(payload.billing),
  }
  delete adjustedPayload.step
  if (!imageTouched.value) {
    adjustedPayload.image = ''
  }

  adjustedPayload.accountName = adjustedPayload.accountName.toLowerCase().replace(/[-\s]/g, '_')

  if (typeof adjustedPayload.a5Account === 'string') {
    adjustedPayload.a5Account = adjustedPayload.a5Account
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num !== '')
      .map(Number)
  }
  return adjustedPayload
}

onMounted(() => {
  VerifyAdmin()
  fetchAccountDetails()
})

watch(a5AccountInput, (newValue) => {
  const filtered = newValue.replace(/[^0-9,]/g, '')

  formFields.value.a5Account = filtered
    .split(',')
    .map((num) => num.trim())
    .filter((num) => num !== '')
    .map(Number)

  a5AccountInput.value = filtered
})

const blockInvalidChars = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', ',']

  if (!/[\d,]/.test(event.key) && !allowedKeys.includes(event.key)) {
    event.preventDefault()
  }
}

const updateA5Account = (value: string) => {
  a5AccountInput.value = value.replace(/[^0-9,]/g, '')
  formFields.value.a5Account = a5AccountInput.value
}

const handleFileChange = (file: File | File[] | null) => {
  const MAX_SIZE_MB = 2
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

  if (file instanceof File) {
    if (file.size > MAX_SIZE_BYTES) {
      alert(`File size exceeds ${MAX_SIZE_MB}MB. Please select a smaller file.`)
      return
    }
    imageTouched.value = true
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      formFields.value.image = reader.result as string
    }
  } else if (Array.isArray(file) && file.length > 0) {
    imageTouched.value = true
    const reader = new FileReader()
    reader.readAsDataURL(file[0])
    reader.onload = () => {
      formFields.value.image = reader.result as string
    }
  } else {
    imageTouched.value = false
    formFields.value.image = ''
  }
}
const tempParent = ref('')
const clearClientOrParent = () => {
  if (formFields.value.isParent) {
    tempParent.value = formFields.value.parentAccount
    formFields.value.parentAccount = ''
  } else {
    formFields.value.parentAccount = tempParent.value
  }
}

const filteredTimezones = computed(() => {
  if (!timezoneSearchQuery.value) return timeZones.value
  return timeZones.value.filter((tz) =>
    tz.toLowerCase().includes(timezoneSearchQuery.value.toLowerCase())
  )
})
const filteredDealerPa = computed(() => {
  if (!searchQueryDealerPa.value) return dealerships
  return dealerships.filter((state) =>
    state.BusinessName.toLowerCase().includes(searchQueryDealerPa?.value?.toLowerCase())
  )
})

const isDirty = computed(() => {
  if (!originalData.value) return false
  const current = JSON.parse(JSON.stringify(formFields.value))
  const original = JSON.parse(JSON.stringify(originalData.value))
  return JSON.stringify(current) !== JSON.stringify(original)
})
</script>

<template>
  <template v-if="isLoading">
    <div class="height-loader">
      <progress-loader></progress-loader>
    </div>
  </template>

  <v-container v-else>
    <v-row class="table-box">
      <v-col cols="12" class="subheader-sticky">
        <div
          style="
            display: flex !important;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
          "
        >
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="text-grey-darken-2"
              @click="router.push(`/account-admin/${accountName}#info`)"
            >
              Back
            </v-btn>
            <h2 class="text-h5 font-weight-medium" style="margin: 0">Edit Account Info</h2>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <v-card flat>
          <v-form ref="formRef">
            <div class="form-grid">
              <div class="fg">
                <v-file-input
                  label="Account Logo"
                  accept="image/png,image/jpeg,image/jpg"
                  variant="outlined"
                  density="comfortable"
                  prepend-icon=""
                  :rules="imageRules"
                  show-size
                  clearable
                  hide-details="auto"
                  @update:modelValue="onLogoChange"
                >
                  <template #prepend>
                    <v-avatar size="48" class="logo-field-avatar" rounded="circle" variant="tonal">
                      <img :src="formFields.image || logoPlaceholder" alt="Account logo" />
                    </v-avatar>
                  </template>

                  <template #append-inner>
                    <v-btn
                      v-if="formFields.image"
                      icon="mdi-close-circle"
                      variant="text"
                      size="small"
                      @click.stop="clearLogo"
                      :aria-label="'Clear logo'"
                    />
                  </template>

                  <template #message>
                    <div class="text-grey-darken-1 text-caption">
                      PNG or JPG, up to {{ IMAGE_MAX_MB }}MB. Drag & drop supported.
                    </div>
                  </template>
                </v-file-input>
              </div>

              <div class="fg">
                <v-text-field
                  data-testid="account-name-friendly-input"
                  v-model="formFields.accountNameFriendly"
                  label="Account Name Friendly"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  hide-details="auto"
                  autofocus
                />
              </div>

              <div class="fg">
                <v-text-field
                  v-model="formFields.mainWebsite"
                  label="Website URL"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  :rules="[urlRule]"
                  hint="Must start with http(s)://"
                  persistent-hint
                  hide-details="auto"
                />
              </div>

              <div class="fg span-2">
                <v-text-field
                  v-model="formFields.address"
                  label="Address"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  placeholder="Street, city, state, zip"
                  hide-details="auto"
                />
              </div>

              <div class="fg">
                <v-combobox
                  v-model="formFields.a5Account"
                  label="Account Id (a5Account)"
                  variant="outlined"
                  density="comfortable"
                  multiple
                  chips
                  hide-selected
                  clearable
                  :items="[]"
                  hint="Press Enter after each number"
                  persistent-hint
                  hide-details="auto"
                  @keydown="blockInvalidChars"
                />
              </div>

              <div class="fg span-2 muted-row">
                <v-checkbox
                  v-model="formFields.isParent"
                  label="Is Parent Account?"
                  @change="clearClientOrParent"
                  density="comfortable"
                  hide-details
                />
              </div>

              <div class="fg">
                <template v-if="formFields.isParent">
                  <v-text-field
                    v-model="formFields.parentAccount"
                    label="Parent Account Name"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :hint="isParentAccount ? 'This is a parent account' : ''"
                    persistent-hint
                    clearable
                  />
                </template>
                <template v-else>
                  <v-select
                    v-model="formFields.parentAccount"
                    :items="clients"
                    item-title="title"
                    item-value="title"
                    label="Parent Account"
                    variant="outlined"
                    density="comfortable"
                    clearable
                    hide-details="auto"
                    :hint="
                      formFields.parentAccount
                        ? `Selected: ${formFields.parentAccount}`
                        : 'Select parent account'
                    "
                    persistent-hint
                  />
                </template>
              </div>

              <div class="fg">
                <v-select
                  v-model="formFields.timezone"
                  :items="filteredTimezones"
                  item-title="label"
                  item-value="value"
                  label="Timezone"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  :no-data-text="'No matching timezone found'"
                  hide-details="auto"
                >
                  <template #prepend-item>
                    <div class="px-4 py-2">
                      <v-text-field
                        v-model="timezoneSearchQuery"
                        label="Search timezones"
                        density="compact"
                        clearable
                        hide-details
                        autofocus
                      />
                    </div>
                    <v-divider />
                  </template>
                </v-select>
              </div>

              <div class="fg">
                <v-select
                  v-model="formFields.accountType"
                  :items="accountTypeItems"
                  label="Account Type"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  hide-details="auto"
                />
              </div>

              <div class="fg">
                <v-text-field
                  v-model="formFields.dealerPA"
                  :label="formFields.accountType === 'Ford Direct' ? 'Dealer PA*' : 'Dealer PA'"
                  variant="outlined"
                  density="comfortable"
                  :disabled="formFields.accountType !== 'Ford Direct' && !formFields.dealerPA"
                  :hint="
                    formFields.accountType === 'Ford Direct'
                      ? 'Required for Ford Direct accounts'
                      : 'Optional'
                  "
                  persistent-hint
                  clearable
                  hide-details="auto"
                />
              </div>

              <div class="fg">
                <v-text-field
                  v-model="formFields.initials"
                  label="Initials"
                  variant="outlined"
                  density="comfortable"
                  disabled
                />
              </div>
              <div class="fg">
                <v-text-field
                  v-model="formFields.accountName"
                  label="Account Name"
                  variant="outlined"
                  density="comfortable"
                  disabled
                />
              </div>
              <div class="fg">
                <v-text-field
                  v-model="formFields.juid"
                  label="JUID"
                  variant="outlined"
                  density="comfortable"
                  disabled
                />
              </div>
              <div class="fg">
                <v-text-field
                  v-model="formFields.countryCode"
                  label="Country Code"
                  variant="outlined"
                  density="comfortable"
                  disabled
                />
              </div>
              <div class="fg span-2">
                <v-text-field
                  v-model="formFields.stateCode"
                  label="State Code"
                  variant="outlined"
                  density="comfortable"
                  disabled
                />
              </div>
            </div>

            <div class="mt-2">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-title>Primary Contact</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <div class="form-grid narrow">
                      <div class="fg">
                        <v-text-field
                          v-model="formFields.primaryContact.name"
                          label="Name"
                          variant="outlined"
                          density="comfortable"
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model="formFields.primaryContact.email"
                          label="Email"
                          variant="outlined"
                          density="comfortable"
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model="formFields.primaryContact.phone"
                          label="Phone"
                          variant="outlined"
                          density="comfortable"
                          clearable
                          hide-details="auto"
                          @input="onPhoneInput('primaryContact.phone')"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>Billing Info</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <div class="form-grid narrow">
                      <div class="fg">
                        <v-text-field
                          v-model="formFields.billing.name"
                          label="Name"
                          variant="outlined"
                          density="comfortable"
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model.trim="formFields.billing.address_1"
                          label="Address 1*"
                          variant="outlined"
                          density="comfortable"
                          :rules="[billingRules.address_1]"
                          required
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model="formFields.billing.address_2"
                          label="Address 2"
                          variant="outlined"
                          density="comfortable"
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model.trim="formFields.billing.city"
                          label="City*"
                          variant="outlined"
                          density="comfortable"
                          :rules="[billingRules.city]"
                          required
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model.trim="formFields.billing.state"
                          label="State*"
                          variant="outlined"
                          density="comfortable"
                          :rules="[billingRules.state]"
                          required
                        />
                      </div>
                      <div class="fg">
                        <v-text-field
                          v-model.trim="formFields.billing.zip"
                          label="Zip*"
                          variant="outlined"
                          density="comfortable"
                          :rules="[billingRules.zip]"
                          required
                        />
                      </div>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>Notes</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-textarea
                      v-model="formFields.notes"
                      label="Notes"
                      variant="outlined"
                      auto-grow
                      rows="3"
                    />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-fade-transition>
      <div class="floating-footer">
        <v-container class="py-2 d-flex justify-end ga-2">
          <v-btn
            data-testid="cancel-btn"
            variant="tonal"
            color="grey"
            @click="router.push(`/account-admin/${accountName}#info`)"
          >
            Cancel
          </v-btn>
          <v-btn
            data-testid="update-btn"
            color="primary"
            :loading="loading"
            :disabled="!isDirty || loading"
            @click="submitForm"
          >
            Update
          </v-btn>
        </v-container>
      </div>
    </v-fade-transition>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.v-form {
  margin-top: 20px;
}
.v-expansion-panel-title {
  font-weight: bold;
}
.mt-4 {
  margin-top: 20px;
}
.height-loader {
  height: 100vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}
.sticky-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px 12px;
  z-index: 3;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.floating-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}
.image-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.image-div {
  height: 60px;
  width: 60px;
  cursor: pointer;
  position: relative;
  border-radius: 50%;
}
.bg-logo {
  object-fit: contain;
  height: 100%;
  width: 100%;
  border-radius: 50%;
}
.plus-icon {
  z-index: 3;
  position: absolute;
  bottom: -4px;
  right: 10px;
  width: 24px;
  height: 24px;
  color: black;
  font-size: 25px;
  background-color: white;
  border-radius: 50%;
  border: 2px solid #9acbd0;
  display: flex;
  justify-content: center;
  align-items: center;
}
.file-input-field {
  display: none;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}
.form-grid.narrow {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.fg {
  display: block;
}
.span-2 {
  grid-column: span 2;
}
.muted-row {
  margin-top: -6px;
}
@media (max-width: 900px) {
  .form-grid,
  .form-grid.narrow {
    grid-template-columns: 1fr;
  }
  .span-2 {
    grid-column: auto;
  }
}
.header-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}
.page-title {
  font-weight: 700;
  font-size: clamp(20px, 2.4vw, 28px);
  margin: 0 8px 0 0;
}
.title-row {
  flex: 1;
}
.logo-trigger {
  position: relative;
  display: inline-block;
  cursor: pointer;
  line-height: 0;
}
.logo-avatar {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06) inset;
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease;
  background: #f6f7f9;
}
.logo-trigger:hover .logo-avatar {
  transform: translateY(-1px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
.logo-trigger img {
  object-fit: contain;
  width: 100%;
  height: 100%;
}
.logo-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.15);
  font-weight: 600;
  display: grid;
  place-items: center;
  font-size: 14px;
  color: #111827;
}
.sr-only-file {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.v-container.mt-2 {
  padding-top: 0;
}
::deep(.v-label .v-field__label--required)::after {
  color: #ef4444;
}
.logo-field-avatar {
  margin-right: 8px;
  background: #f6f7f9;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06) inset;
}
.logo-field-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.table-box {
  margin-bottom: 70px;
}
.subheader-sticky {
  position: sticky;
  top: 56px;
  z-index: 3;
  background: white;
}
@media (max-width: 960px) {
  .subheader-sticky {
    top: 56px;
  }
}
</style>
